"use client";

/**
 * fund-returns-chart.tsx
 *
 * Grouped bar chart comparing a fund's returns across periods (1M / 3M / 6M
 * / 1Y / 3Y CAGR / 5Y CAGR / 10Y). Pure SVG — no Chart.js dependency.
 *
 * V1: benchmark comparison and alpha are deferred — mfdata.in does not expose
 * benchmark index NAV. The prop interface still accepts benchmark/alpha so
 * that when a benchmark fetcher is added later, the chart lights up without
 * component changes.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Performance Analysis section)
 */

import { formatPctSigned } from "@/lib/utils/format";

export interface FundReturnsChartProps {
  returns: {
    oneMonth?: number | null;
    threeMonth?: number | null;
    sixMonth?: number | null;
    oneYear?: number | null;
    threeYearCagr?: number | null;
    fiveYearCagr?: number | null;
    tenYearCagr?: number | null;
    benchmarkOneYear?: number | null;
    benchmarkThreeYear?: number | null;
    alphaOneYear?: number | null;
    alphaThreeYear?: number | null;
  };
  benchmarkName?: string | null;
  fundName: string;
}

type Bar = { label: string; fund: number | null; benchmark: number | null };

const CHART_HEIGHT = 220;
const BAR_GAP = 10;

export function FundReturnsChart({ returns, benchmarkName, fundName }: FundReturnsChartProps) {
  const hasBenchmark =
    returns.benchmarkOneYear !== null &&
    returns.benchmarkOneYear !== undefined;

  const allBars: Bar[] = [
    { label: "1M", fund: returns.oneMonth ?? null, benchmark: null },
    { label: "3M", fund: returns.threeMonth ?? null, benchmark: null },
    { label: "6M", fund: returns.sixMonth ?? null, benchmark: null },
    {
      label: "1Y",
      fund: returns.oneYear ?? null,
      benchmark: returns.benchmarkOneYear ?? null,
    },
    {
      label: "3Y CAGR",
      fund: returns.threeYearCagr ?? null,
      benchmark: returns.benchmarkThreeYear ?? null,
    },
    { label: "5Y CAGR", fund: returns.fiveYearCagr ?? null, benchmark: null },
  ];
  if (returns.tenYearCagr !== null && returns.tenYearCagr !== undefined) {
    allBars.push({ label: "10Y CAGR", fund: returns.tenYearCagr, benchmark: null });
  }

  const bars = allBars.filter((b) => b.fund !== null);

  if (bars.length === 0) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          Return data not available for this fund.
        </p>
      </div>
    );
  }

  // Find max absolute value for scaling (account for negative bars)
  const allValues = bars.flatMap((b) =>
    [b.fund, hasBenchmark ? b.benchmark : null].filter((v): v is number => v !== null)
  );
  const maxVal = Math.max(...allValues.map((v) => Math.abs(v)));
  const scale = maxVal > 0 ? (CHART_HEIGHT - 40) / maxVal : 0;

  const alpha = returns.alphaOneYear ?? returns.alphaThreeYear ?? null;

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      {/* Custom legend */}
      <div className="flex items-center gap-5 mb-5 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-blue" />
          <span className="text-muted-foreground truncate max-w-[220px]" title={fundName}>
            {fundName}
          </span>
        </div>
        {hasBenchmark && benchmarkName && (
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-surface-light" />
            <span className="text-muted-foreground">{benchmarkName}</span>
          </div>
        )}
      </div>

      {/* SVG bar group */}
      <div className="relative overflow-x-auto">
        <div
          className="flex items-end justify-around min-w-full"
          style={{ gap: BAR_GAP, height: CHART_HEIGHT }}
        >
          {bars.map((bar) => {
            const fundPct = bar.fund! * 100;
            const fundHeight = Math.abs(bar.fund!) * scale;
            const benchHeight =
              bar.benchmark !== null ? Math.abs(bar.benchmark) * scale : 0;

            return (
              <div
                key={bar.label}
                className="flex flex-col items-center justify-end flex-1 min-w-[44px]"
                style={{ height: CHART_HEIGHT }}
              >
                <div className="flex items-end gap-1 h-full">
                  {/* Fund bar */}
                  <div
                    className={`w-5 rounded-t-sm ${fundPct >= 0 ? "bg-blue" : "bg-cred"} transition-all duration-500`}
                    style={{ height: Math.max(fundHeight, 2) }}
                  />
                  {/* Benchmark bar */}
                  {hasBenchmark && bar.benchmark !== null && (
                    <div
                      className="w-5 bg-surface-light rounded-t-sm transition-all duration-500"
                      style={{ height: Math.max(benchHeight, 2) }}
                    />
                  )}
                </div>

                {/* Labels below */}
                <div className="mt-2 text-center">
                  <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                    {bar.label}
                  </div>
                  <div
                    className={`text-xs font-bold font-tabular mt-0.5 ${
                      fundPct >= 0 ? "text-foreground" : "text-cred"
                    }`}
                  >
                    {formatPctSigned(bar.fund)}
                  </div>
                  {hasBenchmark && bar.benchmark !== null && (
                    <div className="text-dim text-[10px] font-tabular">
                      {formatPctSigned(bar.benchmark)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Alpha badge */}
      {alpha !== null && alpha !== undefined && (
        <div className="flex justify-end mt-3">
          <span
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
              alpha >= 0
                ? "bg-cgreen/15 text-cgreen"
                : "bg-cred/15 text-cred"
            }`}
          >
            Alpha: {formatPctSigned(alpha)}
          </span>
        </div>
      )}
    </div>
  );
}
